import React, { useState, useEffect, useRef } from 'react';
import { 
  MousePointer2, 
  PenTool, 
  Eraser, 
  Copy, 
  Lock, 
  Unlock,
  Group,
  SquareDashed,
  Undo,
  Redo,
  GripVertical,
  Hand,
  Folder,
  Save,
  FolderOpen,
  Download,
  Upload,
  Sparkles,
  FileImage,
  FileCode,
  Grid3x3,
  AlignLeft,
  AlignCenter,
  AlignRight,
  AlignVerticalJustifyCenter,
  AlignHorizontalJustifyCenter,
  MoreVertical,
  Search,
  Network,
  Layout,
  Eye,
  Link,
  Settings,
  Type
} from 'lucide-react';

export type Tool = 'select' | 'hand' | 'draw' | 'eraser' | 'connect' | 'text';

interface ToolbarProps {
  currentTool: Tool;
  setTool: (t: Tool) => void;
  onDuplicate: () => void;
  onToggleLock: () => void;
  onGroup: () => void;
  onUndo: () => void;
  onRedo: () => void;
  hasSelection: boolean;
  isLocked: boolean;
  canUndo: boolean;
  canRedo: boolean;
  // New project/export handlers
  onSave?: () => void;
  onSaveAs?: () => void;
  onOpenProjects?: () => void;
  onNewProject?: () => void;
  onExportPNG?: () => void;
  onExportSVG?: () => void;
  onExportJSON?: () => void;
  onExportYAML?: () => void;
  onImportJSON?: () => void;
  onOpenTemplates?: () => void;
  saveStatus?: 'saved' | 'saving' | 'unsaved';
  // Alignment & Grid
  onAlign?: (type: 'align-left' | 'align-center' | 'align-right' | 'align-top' | 'align-middle' | 'align-bottom' | 'distribute-horizontal' | 'distribute-vertical' | 'match-width' | 'match-height') => void;
  gridEnabled?: boolean;
  onToggleGrid?: () => void;
  snapToGrid?: boolean;
  onToggleSnapToGrid?: () => void;
  onGridSizeChange?: (size: number) => void;
  // Search
  onOpenSearch?: () => void;
  // Layout
  onAutoLayout?: (algorithm: 'hierarchical' | 'force-directed' | 'grid' | 'layered') => void;
  // Layers
  onOpenLayers?: () => void;
  // Templates
  onSaveAsTemplate?: () => void;
  // Terraform Export
  onExportTerraform?: (provider: 'aws' | 'gcp' | 'azure') => void;
  // Kubernetes Export
  onExportKubernetes?: () => void;
  // Settings
  onOpenSettings?: () => void;
}

const Toolbar: React.FC<ToolbarProps> = ({ 
  currentTool, 
  setTool, 
  onDuplicate, 
  onToggleLock,
  onGroup,
  onUndo,
  onRedo,
  hasSelection,
  isLocked,
  canUndo,
  canRedo,
  onSave,
  onSaveAs,
  onOpenProjects,
  onNewProject,
  onExportPNG,
  onExportSVG,
  onExportJSON,
  onExportYAML,
  onImportJSON,
  onOpenTemplates,
  saveStatus = 'saved',
  onAlign,
  gridEnabled = false,
  onToggleGrid,
  snapToGrid = false,
  onToggleSnapToGrid,
  onGridSizeChange,
  onOpenSearch,
  onAutoLayout,
  onOpenLayers,
  onSaveAsTemplate,
  onExportTerraform,
  onExportKubernetes,
  onOpenSettings
}) => {
  const [position, setPosition] = useState<{ x: number; y: number } | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const dragOffset = useRef({ x: 0, y: 0 });
  const toolbarRef = useRef<HTMLDivElement>(null);
  const [showProjectsMenu, setShowProjectsMenu] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [showAlignmentMenu, setShowAlignmentMenu] = useState(false);
  const [showGridMenu, setShowGridMenu] = useState(false);
  const [showLayoutMenu, setShowLayoutMenu] = useState(false);

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!toolbarRef.current) return;
    
    setIsDragging(true);
    const rect = toolbarRef.current.getBoundingClientRect();
    const parentRect = toolbarRef.current.offsetParent?.getBoundingClientRect() || { left: 0, top: 0 };
    
    dragOffset.current = {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    };

    if (!position) {
       setPosition({
         x: rect.left - parentRect.left,
         y: rect.top - parentRect.top
       });
    }
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (isDragging && toolbarRef.current) {
        e.preventDefault(); 
        
        const parentRect = toolbarRef.current.offsetParent?.getBoundingClientRect() || { left: 0, top: 0 };
        
        const newX = e.clientX - parentRect.left - dragOffset.current.x;
        const newY = e.clientY - parentRect.top - dragOffset.current.y;
        
        setPosition({ x: newX, y: newY });
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging]);

  return (
    <div 
      ref={toolbarRef}
      className={`absolute bg-white border border-black/10 rounded-lg shadow-[0px_4px_12px_rgba(0,0,0,0.1)] p-1.5 flex items-center gap-1 z-[60] transition-shadow ${isDragging ? 'cursor-grabbing shadow-xl ring-1 ring-black/5' : ''} ${!position ? 'bottom-6 left-1/2 transform -translate-x-1/2' : ''}`}
      style={position ? { left: position.x, top: position.y } : undefined}
    >
      {/* Drag Handle */}
      <div 
        className={`p-1 flex items-center justify-center rounded transition-colors ${isDragging ? 'cursor-grabbing text-gray-500' : 'cursor-grab text-gray-300 hover:text-gray-500 hover:bg-gray-50'}`}
        onMouseDown={handleMouseDown}
        title="Drag Toolbar"
      >
        <GripVertical size={14} />
      </div>

      <div className="w-px h-6 bg-gray-200 mx-1"></div>

      <button
        onClick={() => setTool('select')}
        className={`p-2 rounded-md transition-all duration-150 active:scale-95 ${currentTool === 'select' ? 'bg-black text-white shadow-md' : 'text-gray-500 hover:bg-gray-100 hover:text-black hover:scale-105'}`}
        title="Select (V)"
      >
        <MousePointer2 size={18} />
      </button>

      <button
        onClick={() => setTool('hand')}
        className={`p-2 rounded-md transition-all duration-150 active:scale-95 ${currentTool === 'hand' ? 'bg-black text-white shadow-md' : 'text-gray-500 hover:bg-gray-100 hover:text-black hover:scale-105'}`}
        title="Pan Tool (H)"
      >
        <Hand size={18} />
      </button>
      
      <button
        onClick={() => setTool('draw')}
        className={`p-2 rounded-md transition-all duration-150 active:scale-95 ${currentTool === 'draw' ? 'bg-black text-white shadow-md' : 'text-gray-500 hover:bg-gray-100 hover:text-black hover:scale-105'}`}
        title="Draw (P)"
      >
        <PenTool size={18} />
      </button>

      <button
        onClick={() => setTool('eraser')}
        className={`p-2 rounded-md transition-all duration-150 active:scale-95 ${currentTool === 'eraser' ? 'bg-black text-white shadow-md' : 'text-gray-500 hover:bg-gray-100 hover:text-black hover:scale-105'}`}
        title="Eraser (E)"
      >
        <Eraser size={18} />
      </button>

      <button
        onClick={() => setTool('connect')}
        className={`p-2 rounded-md transition-all duration-150 active:scale-95 ${currentTool === 'connect' ? 'bg-black text-white shadow-md' : 'text-gray-500 hover:bg-gray-100 hover:text-black hover:scale-105'}`}
        title="Connect (C)"
      >
        <Link size={18} />
      </button>

      <button
        onClick={() => setTool('text')}
        className={`p-2 rounded-md transition-all duration-150 active:scale-95 ${currentTool === 'text' ? 'bg-black text-white shadow-md' : 'text-gray-500 hover:bg-gray-100 hover:text-black hover:scale-105'}`}
        title="Text (T)"
      >
        <Type size={18} />
      </button>

      <div className="w-px h-6 bg-gray-200 mx-1"></div>

      <button
        onClick={onUndo}
        disabled={!canUndo}
        className="p-2 rounded-md text-gray-500 hover:bg-gray-100 hover:text-black disabled:opacity-30 disabled:cursor-not-allowed transition-all duration-150 active:scale-95 hover:scale-105"
        title="Undo (Ctrl+Z)"
      >
        <Undo size={18} />
      </button>

      <button
        onClick={onRedo}
        disabled={!canRedo}
        className="p-2 rounded-md text-gray-500 hover:bg-gray-100 hover:text-black disabled:opacity-30 disabled:cursor-not-allowed transition-all duration-150 active:scale-95 hover:scale-105"
        title="Redo (Ctrl+Y)"
      >
        <Redo size={18} />
      </button>

      <div className="w-px h-6 bg-gray-200 mx-1"></div>

      <button
        onClick={onDuplicate}
        disabled={!hasSelection}
        className="p-2 rounded-md text-gray-500 hover:bg-gray-100 hover:text-black disabled:opacity-30 disabled:cursor-not-allowed transition-all duration-150 active:scale-95 hover:scale-105"
        title="Duplicate (Ctrl+D)"
      >
        <Copy size={18} />
      </button>

      <button
        onClick={onGroup}
        disabled={!hasSelection}
        className="p-2 rounded-md text-gray-500 hover:bg-gray-100 hover:text-black disabled:opacity-30 disabled:cursor-not-allowed transition-all duration-150 active:scale-95 hover:scale-105"
        title="Group Selection (Ctrl+G)"
      >
        <SquareDashed size={18} />
      </button>

      <button
        onClick={onToggleLock}
        disabled={!hasSelection}
        className="p-2 rounded-md text-gray-500 hover:bg-gray-100 hover:text-black disabled:opacity-30 disabled:cursor-not-allowed transition-all duration-150 active:scale-95 hover:scale-105"
        title={isLocked ? "Unlock" : "Lock"}
      >
        {isLocked ? <Unlock size={18} /> : <Lock size={18} />}
      </button>

      <div className="w-px h-6 bg-gray-200 mx-1"></div>

      {/* Alignment Menu */}
      {onAlign && (
        <div className="relative">
          <button
            onClick={() => {
              setShowAlignmentMenu(!showAlignmentMenu);
              setShowGridMenu(false);
            }}
            disabled={!hasSelection}
            className="p-2 rounded-md text-gray-500 hover:bg-gray-100 hover:text-black disabled:opacity-30 disabled:cursor-not-allowed transition-all duration-150 active:scale-95 hover:scale-105"
            title="Alignment Tools"
          >
            <AlignLeft size={18} />
          </button>
          
          {showAlignmentMenu && (
            <>
              <div 
                className="fixed inset-0 z-[70]" 
                onClick={() => setShowAlignmentMenu(false)}
              />
              <div className="absolute top-full left-0 mt-2 bg-white border border-black/10 rounded-lg shadow-xl py-1 min-w-[200px] z-[80]">
                <div className="px-3 py-2 text-xs font-bold text-gray-500 uppercase border-b border-gray-100">Align</div>
                <button
                  onClick={() => { onAlign('align-left'); setShowAlignmentMenu(false); }}
                  className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 hover:text-black flex items-center gap-2 transition-all duration-150 active:scale-95"
                >
                  <AlignLeft size={16} />
                  Align Left
                </button>
                <button
                  onClick={() => { onAlign('align-center'); setShowAlignmentMenu(false); }}
                  className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 hover:text-black flex items-center gap-2 transition-all duration-150 active:scale-95"
                >
                  <AlignCenter size={16} />
                  Align Center
                </button>
                <button
                  onClick={() => { onAlign('align-right'); setShowAlignmentMenu(false); }}
                  className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 hover:text-black flex items-center gap-2 transition-all duration-150 active:scale-95"
                >
                  <AlignRight size={16} />
                  Align Right
                </button>
                <div className="h-px bg-gray-200 my-1" />
                <button
                  onClick={() => { onAlign('align-top'); setShowAlignmentMenu(false); }}
                  className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 hover:text-black flex items-center gap-2 transition-all duration-150 active:scale-95"
                >
                  <AlignVerticalJustifyCenter size={16} className="rotate-180" />
                  Align Top
                </button>
                <button
                  onClick={() => { onAlign('align-middle'); setShowAlignmentMenu(false); }}
                  className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 hover:text-black flex items-center gap-2 transition-all duration-150 active:scale-95"
                >
                  <AlignHorizontalJustifyCenter size={16} />
                  Align Middle
                </button>
                <button
                  onClick={() => { onAlign('align-bottom'); setShowAlignmentMenu(false); }}
                  className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 hover:text-black flex items-center gap-2 transition-all duration-150 active:scale-95"
                >
                  <AlignVerticalJustifyCenter size={16} />
                  Align Bottom
                </button>
                <div className="h-px bg-gray-200 my-1" />
                <button
                  onClick={() => { onAlign('distribute-horizontal'); setShowAlignmentMenu(false); }}
                  className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 hover:text-black flex items-center gap-2 transition-all duration-150 active:scale-95"
                >
                  <MoreVertical size={16} />
                  Distribute Horizontally
                </button>
                <button
                  onClick={() => { onAlign('distribute-vertical'); setShowAlignmentMenu(false); }}
                  className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 hover:text-black flex items-center gap-2 transition-all duration-150 active:scale-95"
                >
                  <MoreVertical size={16} className="rotate-90" />
                  Distribute Vertically
                </button>
                <div className="h-px bg-gray-200 my-1" />
                <button
                  onClick={() => { onAlign('match-width'); setShowAlignmentMenu(false); }}
                  className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 hover:text-black flex items-center gap-2 transition-all duration-150 active:scale-95"
                >
                  Match Width
                </button>
                <button
                  onClick={() => { onAlign('match-height'); setShowAlignmentMenu(false); }}
                  className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 hover:text-black flex items-center gap-2 transition-all duration-150 active:scale-95"
                >
                  Match Height
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {/* Grid Menu */}
      {onToggleGrid && (
        <div className="relative">
          <button
            onClick={() => {
              setShowGridMenu(!showGridMenu);
              setShowAlignmentMenu(false);
            }}
            className={`p-2 rounded-md transition-all ${gridEnabled ? 'bg-black text-white' : 'text-gray-500 hover:bg-gray-100 hover:text-black'}`}
            title="Grid Settings"
          >
            <Grid3x3 size={18} />
          </button>
          
          {showGridMenu && (
            <>
              <div 
                className="fixed inset-0 z-[70]" 
                onClick={() => setShowGridMenu(false)}
              />
              <div className="absolute top-full left-0 mt-2 bg-white border border-black/10 rounded-lg shadow-xl py-1 min-w-[180px] z-[80]">
                <button
                  onClick={() => { onToggleGrid(); setShowGridMenu(false); }}
                  className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 hover:text-black flex items-center gap-2 transition-all duration-150 active:scale-95"
                >
                  <Grid3x3 size={16} />
                  {gridEnabled ? 'Hide Grid' : 'Show Grid'}
                </button>
                {onToggleSnapToGrid && (
                  <button
                    onClick={() => { onToggleSnapToGrid(); setShowGridMenu(false); }}
                    className={`w-full px-4 py-2 text-left text-sm flex items-center gap-2 transition-colors ${snapToGrid ? 'bg-gray-100 text-black' : 'text-gray-700 hover:bg-gray-100 hover:text-black'}`}
                  >
                    {snapToGrid ? '✓ ' : ''}Snap to Grid
                  </button>
                )}
                {onGridSizeChange && (
                  <>
                    <div className="h-px bg-gray-200 my-1" />
                    <div className="px-4 py-2 text-xs font-bold text-gray-500 uppercase">Grid Size</div>
                    {[10, 20, 50].map(size => (
                      <button
                        key={size}
                        onClick={() => { onGridSizeChange(size); setShowGridMenu(false); }}
                        className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 hover:text-black transition-colors"
                      >
                        {size}px
                      </button>
                    ))}
                  </>
                )}
              </div>
            </>
          )}
        </div>
      )}

      <div className="w-px h-6 bg-gray-200 mx-1"></div>

      {/* Auto Layout Menu */}
      {onAutoLayout && (
        <div className="relative">
          <button
            onClick={() => {
              setShowLayoutMenu(!showLayoutMenu);
              setShowAlignmentMenu(false);
              setShowGridMenu(false);
            }}
            className="p-2 rounded-md text-gray-500 hover:bg-gray-100 hover:text-black transition-all duration-150 active:scale-95 hover:scale-105"
            title="Auto Layout (Ctrl+L)"
          >
            <Layout size={18} />
          </button>
          
          {showLayoutMenu && (
            <>
              <div 
                className="fixed inset-0 z-[70]" 
                onClick={() => setShowLayoutMenu(false)}
              />
              <div className="absolute top-full left-0 mt-2 bg-white border border-black/10 rounded-lg shadow-xl py-1 min-w-[200px] z-[80]">
                <div className="px-3 py-2 text-xs font-bold text-gray-500 uppercase border-b border-gray-100">Layout Algorithm</div>
                <button
                  onClick={() => { onAutoLayout('hierarchical'); setShowLayoutMenu(false); }}
                  className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 hover:text-black flex items-center gap-2 transition-all duration-150 active:scale-95"
                >
                  <Network size={16} />
                  Hierarchical
                </button>
                <button
                  onClick={() => { onAutoLayout('force-directed'); setShowLayoutMenu(false); }}
                  className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 hover:text-black flex items-center gap-2 transition-all duration-150 active:scale-95"
                >
                  <Network size={16} />
                  Force-Directed
                </button>
                <button
                  onClick={() => { onAutoLayout('grid'); setShowLayoutMenu(false); }}
                  className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 hover:text-black flex items-center gap-2 transition-all duration-150 active:scale-95"
                >
                  <Grid3x3 size={16} />
                  Grid
                </button>
                <button
                  onClick={() => { onAutoLayout('layered'); setShowLayoutMenu(false); }}
                  className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 hover:text-black flex items-center gap-2 transition-all duration-150 active:scale-95"
                >
                  <Layout size={16} />
                  Layered
                </button>
              </div>
            </>
          )}
        </div>
      )}

      <div className="w-px h-6 bg-gray-200 mx-1"></div>

      {/* Search */}
      {onOpenSearch && (
        <button
          onClick={onOpenSearch}
          className="p-2 rounded-md text-gray-500 hover:bg-gray-100 hover:text-black transition-all"
          title="Search Components (Ctrl+F)"
        >
          <Search size={18} />
        </button>
      )}

      {/* Layers */}
      {onOpenLayers && (
        <button
          onClick={onOpenLayers}
          className="p-2 rounded-md text-gray-500 hover:bg-gray-100 hover:text-black transition-all"
          title="Layers"
        >
          <Eye size={18} />
        </button>
      )}

      <div className="w-px h-6 bg-gray-200 mx-1"></div>

      {/* Projects Menu */}
      <div className="relative">
        <button
          onClick={() => {
            setShowProjectsMenu(!showProjectsMenu);
            setShowExportMenu(false); // Close export menu
          }}
          className="p-2 rounded-md text-gray-500 hover:bg-gray-100 hover:text-black transition-all flex items-center gap-1"
          title="Projects"
        >
          <Folder size={18} />
          {saveStatus === 'saving' && <span className="text-[10px] text-blue-600">...</span>}
          {saveStatus === 'unsaved' && <span className="text-[10px] text-orange-600">*</span>}
        </button>
        
        {showProjectsMenu && (
          <>
            <div 
              className="fixed inset-0 z-[70]" 
              onClick={() => setShowProjectsMenu(false)}
            />
            <div className="absolute top-full left-0 mt-2 bg-white border border-black/10 rounded-lg shadow-xl py-1 min-w-[180px] z-[80]">
              <button
                onClick={() => { onNewProject?.(); setShowProjectsMenu(false); }}
                className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 hover:text-black flex items-center gap-2 transition-all duration-150 active:scale-95"
              >
                <FileCode size={16} />
                New Project
              </button>
              <button
                onClick={() => { onSave?.(); setShowProjectsMenu(false); }}
                className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 hover:text-black flex items-center gap-2 transition-all duration-150 active:scale-95"
              >
                <Save size={16} />
                Save (Ctrl+S)
              </button>
              <button
                onClick={() => { onSaveAs?.(); setShowProjectsMenu(false); }}
                className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 hover:text-black flex items-center gap-2 transition-all duration-150 active:scale-95"
              >
                <Save size={16} />
                Save As...
              </button>
              <div className="h-px bg-gray-200 my-1" />
              <button
                onClick={() => { onOpenProjects?.(); setShowProjectsMenu(false); }}
                className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 hover:text-black flex items-center gap-2 transition-all duration-150 active:scale-95"
              >
                <FolderOpen size={16} />
                Open Projects...
              </button>
              <button
                onClick={() => { onImportJSON?.(); setShowProjectsMenu(false); }}
                className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 hover:text-black flex items-center gap-2 transition-all duration-150 active:scale-95"
              >
                <Upload size={16} />
                Import JSON
              </button>
            </div>
          </>
        )}
      </div>

      {/* Export Menu */}
      <div className="relative">
        <button
          onClick={() => {
            setShowExportMenu(!showExportMenu);
            setShowProjectsMenu(false); // Close projects menu
          }}
          className="p-2 rounded-md text-gray-500 hover:bg-gray-100 hover:text-black transition-all"
          title="Export"
        >
          <Download size={18} />
        </button>
        
        {showExportMenu && (
          <>
            <div 
              className="fixed inset-0 z-[70]" 
              onClick={() => setShowExportMenu(false)}
            />
            <div className="absolute top-full left-0 mt-2 bg-white border border-black/10 rounded-lg shadow-xl py-1 min-w-[180px] z-[80]">
              <button
                onClick={() => { onExportPNG?.(); setShowExportMenu(false); }}
                className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 hover:text-black flex items-center gap-2 transition-all duration-150 active:scale-95"
              >
                <FileImage size={16} />
                Export PNG
              </button>
              <button
                onClick={() => { onExportSVG?.(); setShowExportMenu(false); }}
                className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 hover:text-black flex items-center gap-2 transition-all duration-150 active:scale-95"
              >
                <FileImage size={16} />
                Export SVG
              </button>
              <div className="h-px bg-gray-200 my-1" />
              <button
                onClick={() => { onExportJSON?.(); setShowExportMenu(false); }}
                className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 hover:text-black flex items-center gap-2 transition-all duration-150 active:scale-95"
              >
                <FileCode size={16} />
                Export JSON
              </button>
              <button
                onClick={() => { onExportYAML?.(); setShowExportMenu(false); }}
                className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 hover:text-black flex items-center gap-2 transition-all duration-150 active:scale-95"
              >
                <FileCode size={16} />
                Export YAML
              </button>
              {onExportTerraform && (
                <>
                  <div className="h-px bg-gray-200 my-1" />
                  <div className="px-3 py-2 text-xs font-bold text-gray-500 uppercase">Infrastructure</div>
                  <button
                    onClick={() => { onExportTerraform('aws'); setShowExportMenu(false); }}
                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 hover:text-black flex items-center gap-2 transition-all duration-150 active:scale-95"
                  >
                    <FileCode size={16} />
                    Export Terraform (AWS)
                  </button>
                  <button
                    onClick={() => { onExportTerraform('gcp'); setShowExportMenu(false); }}
                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 hover:text-black flex items-center gap-2 transition-all duration-150 active:scale-95"
                  >
                    <FileCode size={16} />
                    Export Terraform (GCP)
                  </button>
                  <button
                    onClick={() => { onExportTerraform('azure'); setShowExportMenu(false); }}
                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 hover:text-black flex items-center gap-2 transition-all duration-150 active:scale-95"
                  >
                    <FileCode size={16} />
                    Export Terraform (Azure)
                  </button>
                  {onExportKubernetes && (
                    <button
                      onClick={() => { onExportKubernetes(); setShowExportMenu(false); }}
                      className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 hover:text-black flex items-center gap-2 transition-all duration-150 active:scale-95"
                    >
                      <FileCode size={16} />
                      Export Kubernetes
                    </button>
                  )}
                </>
              )}
            </div>
          </>
        )}
      </div>

      {/* Templates Button */}
      <div className="relative">
        {onOpenTemplates && (
          <button
            onClick={() => {
              onOpenTemplates();
              setShowProjectsMenu(false);
              setShowExportMenu(false);
            }}
            className="p-2 rounded-md text-purple-600 hover:bg-purple-50 hover:text-purple-700 transition-all"
            title="Architecture Templates"
          >
            <Sparkles size={18} />
          </button>
        )}
        {onSaveAsTemplate && (
          <div className="absolute top-full left-0 mt-1 opacity-0 hover:opacity-100 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto transition-opacity">
            <div className="bg-white border border-black/10 rounded-lg shadow-xl py-1 min-w-[180px] z-[80]">
              <button
                onClick={() => { onSaveAsTemplate(); setShowProjectsMenu(false); setShowExportMenu(false); }}
                className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 hover:text-black flex items-center gap-2 transition-all duration-150 active:scale-95"
              >
                <Sparkles size={16} />
                Save as Template
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Settings Button */}
      {onOpenSettings && (
        <>
          <div className="w-px h-6 bg-gray-200 mx-1"></div>
          <button
            onClick={() => {
              onOpenSettings();
              setShowProjectsMenu(false);
              setShowExportMenu(false);
            }}
            className="p-2 rounded-md text-gray-500 hover:bg-gray-100 hover:text-black transition-all duration-150 active:scale-95 hover:scale-105"
            title="Settings (Ctrl+,)"
          >
            <Settings size={18} />
          </button>
        </>
      )}
    </div>
  );
};

export default Toolbar;